import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RenameCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  initialName: string;
  onSave: (newName: string, parentId?: string) => Promise<void>;
  parentOptions?: Array<{ id: string; name: string }>;
  initialParentId?: string;
}

export function RenameCategoryDialog({ open, onClose, initialName, onSave, parentOptions = [], initialParentId = '' }: RenameCategoryDialogProps) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState(initialName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parentId, setParentId] = useState(initialParentId);

  useEffect(() => {
    if (open) {
      setNewName(initialName);
      setParentId(initialParentId);
    }
  }, [open, initialName, initialParentId]);

  const handleSave = async () => {
    if (!newName.trim()) return;
    setIsSubmitting(true);
    try {
      await onSave(newName.trim(), parentId);
      onClose();
    } catch (error) {
      console.error('Failed to rename category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('entity_list.codex.tabs.rename')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('common.name')}
          fullWidth
          variant="outlined"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={isSubmitting}
        />
        <TextField select margin="dense" label={t('entity_list.codex.tabs.parent_category_optional')} fullWidth value={parentId} onChange={(e) => setParentId(e.target.value)} disabled={isSubmitting}>
          <MenuItem value="">{t('entity_list.codex.tabs.none_top_level')}</MenuItem>
          {parentOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSubmitting}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
